import { WebsitePage } from './features/website/WebsitePage';
import { SupportPage } from './features/support/SupportPage';
import { LearningPlansPage } from './features/learning-plans/LearningPlansPage';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './auth/AuthContext';
import { ProtectedRoute } from './auth/ProtectedRoute';
import { Layout } from './components/Layout';
import { ThemeProvider } from './components/ThemeToggle';
import { ToastProvider } from './components/Toast';
import { EmptyState } from './components/EmptyState';
import { AccountPage } from './features/account/AccountPage';
import { LoginPage } from './features/auth/LoginPage';
import { DashboardPage } from './features/dashboard/DashboardPage';
import { UsersPage } from './features/users/UsersPage';
import { UserDetailPage } from './features/users/UserDetailPage';
import { SessionsPage } from './features/sessions/SessionsPage';
import { SessionDetailPage } from './features/sessions/SessionDetailPage';
import { AuditLogPage } from './features/audit/AuditLogPage';
import { TrialsPage } from './features/funnel/TrialsPage';
import { TutorApplicationsPage } from './features/tutors/TutorApplicationsPage';
import { SubscriptionsPage } from './features/funnel/SubscriptionsPage';
import { ActingAsPage } from './features/impersonation/ActingAsPage';
import { ApiError } from './lib/api';
import { Page } from './components/Layout';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (failureCount, error) => {
        // Never retry a rejection the server meant: a 401/403/404 will not
        // become a 200 on the third attempt, and retrying just delays the
        // message the operator needs to see.
        if (error instanceof ApiError && error.status < 500) return false;
        return failureCount < 2;
      },
    },
    mutations: { retry: false },
  },
});

function NotFound() {
  return (
    <Page>
      <EmptyState
        title="There is nothing at this address"
        description="The link may be out of date, or the record may have been removed."
      />
    </Page>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <ToastProvider>
          <BrowserRouter>
            <AuthProvider>
              <Routes>
                <Route path="/login" element={<LoginPage />} />

                <Route
                  element={
                    // Any signed-in user reaches the shell; individual
                    // routes decide whether they need an admin.
                    <ProtectedRoute adminOnly={false}>
                      <Layout />
                    </ProtectedRoute>
                  }
                >
                  <Route path="website" element={<ProtectedRoute><WebsitePage /></ProtectedRoute>} />
                  <Route path="support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
                  <Route path="learning-plans" element={<ProtectedRoute><LearningPlansPage /></ProtectedRoute>} />
                  <Route
                    index
                    element={
                      <ProtectedRoute>
                        <DashboardPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="users"
                    element={
                      <ProtectedRoute>
                        <UsersPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="users/:id"
                    element={
                      <ProtectedRoute>
                        <UserDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="sessions"
                    element={
                      <ProtectedRoute>
                        <SessionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="sessions/:id"
                    element={
                      <ProtectedRoute>
                        <SessionDetailPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="tutor-applications"
                    element={
                      <ProtectedRoute>
                        <TutorApplicationsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="trials"
                    element={
                      <ProtectedRoute>
                        <TrialsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="subscriptions"
                    element={
                      <ProtectedRoute>
                        <SubscriptionsPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="audit"
                    element={
                      <ProtectedRoute>
                        <AuditLogPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route
                    path="account"
                    element={
                      <ProtectedRoute>
                        <AccountPage />
                      </ProtectedRoute>
                    }
                  />
                  <Route path="acting-as" element={<ActingAsPage />} />
                  <Route path="*" element={<NotFound />} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </AuthProvider>
          </BrowserRouter>
        </ToastProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
